import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DeleteModalWithConfirmInput } from '@/lib/Modals';
import { IModalContentProps } from '@/models/modals';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useDeleteEmailTemplateMutation } from '@/services/endpoints/campaigns';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export type DeleteEmailTemplateModalProps = {
  template: { id: string; name: string };
} & IModalContentProps;

export const DeleteEmailTemplateModal: FC<DeleteEmailTemplateModalProps> = ({ template, closeModal }) => {
  const { t } = useTranslation('campaign');
  const workspace = useWorkspace();
  const dispatch = useAppDispatch();
  const [deleteEmailTemplate, { isLoading }] = useDeleteEmailTemplateMutation();

  const handleDelete = async () => {
    if (!workspace.id || !template.id) {
      return;
    }

    try {
      await deleteEmailTemplate({
        workspaceId: workspace.id,
        templateId: template.id,
      }).unwrap();

      closeModal();

      dispatch(
        showToast({
          type: ToastType.SUCCESS,
          title: t('whatsappTemplates.templateDeleted'),
          message: t('whatsappTemplates.templateRemovedSuccessfully', { templateName: template.name }),
        })
      );
    } catch {
      dispatch(
        showToast({
          type: ToastType.ERROR,
          title: t('whatsappTemplates.templateDeleteFailedTitle'),
          message: t('whatsappTemplates.templateDeleteFailedMessage'),
        })
      );
    }
  };

  return (
    <DeleteModalWithConfirmInput
      inputComparisonValue={template.name}
      confirmText='campaign.whatsappTemplates.confirmDeleteTemplate'
      onConfirm={handleDelete}
      onClose={closeModal}
      isLoading={isLoading}
    >
      <DeleteModalWithConfirmInput.Title>{t('whatsappTemplates.deleteThisTemplate')}</DeleteModalWithConfirmInput.Title>
      <DeleteModalWithConfirmInput.Description>{t('whatsappTemplates.deleteTemplateWarning')}</DeleteModalWithConfirmInput.Description>

      <DeleteModalWithConfirmInput.InputHint>
        <Trans
          i18nKey='campaign:whatsappTemplates.deleteTemplateConfirmation'
          components={{ 1: <strong /> }}
          values={{ name: template.name }}
        />
      </DeleteModalWithConfirmInput.InputHint>
    </DeleteModalWithConfirmInput>
  );
};
